/* eslint-disable react-hooks/refs -- the `prevWorkspaceMembersRef` /
 * `prevWorkspaceChannelsRef` pattern is an intentional structural-sharing
 * guard: read previous projection at render-time, compare against next, and
 * either return the previous reference (preserving identity for downstream
 * memos) or write the new value through. Any other shape (`useMemo` over
 * the input array, `JSON.stringify`, etc.) breaks the identity contract
 * `MessageInput` + `MessageRow.memo` rely on. */
import { useMemo, useRef } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { useQueryDebugLog } from "@/hooks/use-debug-query";
import { queries } from "@/lib/queries";
import { RENDER_DEBUG } from "@/lib/debug-flags";

interface RenderUser {
  id: string;
  display_name: string;
  avatar_url?: string | null;
  is_coworker?: boolean | null;
  is_deactivated?: boolean | null;
}

interface UseWorkspaceMentionsDataArgs {
  workspaceId: string | null;
  channelId: string | null;
  /** From `useRenderUserMap` — used to resolve workspace member display names. */
  renderUserMap: ReadonlyMap<string, RenderUser>;
  /** True iff the active channel is a 1:1 or group DM. */
  isDM: boolean;
  /** Resolved channel members; used to compute `hasCollaborators`. */
  channelMemberList: readonly { user_id: string }[];
}

interface MemberLite {
  id: string;
  display_name: string;
  avatar_url?: string | null;
  is_coworker?: boolean | null;
}
interface ChannelLite {
  id: string;
  name: string;
}

/**
 * Owns the two workspace-scoped autocomplete sources used by
 * `MessageInput` mentions, `MessageRow` highlighting, and
 * `ThreadPanel`:
 *
 * - `workspaceMembers` — active (non-deactivated) workspace users
 *   shaped down to `{ id, display_name }`.
 * - `workspaceChannels` — non-DM, non-archived channels shaped down
 *   to `{ id, name }`.
 *
 * Both lists are projected through a "previous-equals-next?"
 * structural-sharing guard so a Zero re-emission with structurally-
 * identical content returns the previous array reference. That keeps
 * `MessageInput`'s mention dropdown from rebuilding its index on
 * every typing heartbeat, and `MessageRow`'s `memo()` from busting
 * on shape-only churn.
 *
 * Also exposes `wsMembers` (the raw workspace_members rows, including
 * `role` and `removed_at`) and `wsMembersStatus` so callers can
 * compute downstream views like "active member id set" + "has
 * collaborators".
 */
export function useWorkspaceMentionsData({
  workspaceId,
  channelId,
  renderUserMap,
  isDM,
  channelMemberList,
}: UseWorkspaceMentionsDataArgs) {
  // Workspace members for mentions
  const wsMembersResult = useQuery(
    workspaceId && channelId
      ? queries.workspace_members.byWorkspace(workspaceId)
      : null,
  );
  useQueryDebugLog("workspace_members.byWorkspace", wsMembersResult);
  const [wsMembersRaw, wsMembersStatus] = wsMembersResult;
  const wsMembers = useMemo(() => wsMembersRaw ?? [], [wsMembersRaw]);
  const prevWorkspaceMembersRef = useRef<MemberLite[]>([]);
  const workspaceMembers = useMemo<MemberLite[]>(() => {
    const next: MemberLite[] = wsMembers
      .map((m) => renderUserMap.get(m.user_id))
      .filter((u): u is NonNullable<typeof u> => !!u && !u.is_deactivated)
      .map((u) => ({
        id: u.id,
        display_name: u.display_name,
        avatar_url: u.avatar_url ?? null,
        is_coworker: u.is_coworker ?? null,
      }));
    const prev = prevWorkspaceMembersRef.current;
    if (
      next.length === prev.length &&
      next.every(
        (n, i) =>
          n.id === prev[i].id &&
          n.display_name === prev[i].display_name &&
          n.avatar_url === prev[i].avatar_url &&
          n.is_coworker === prev[i].is_coworker,
      )
    ) {
      if (RENDER_DEBUG)
        console.debug(`[hook:workspaceMembers] STABLE size=${next.length}`);
      return prev;
    }
    if (RENDER_DEBUG)
      console.debug(
        `[hook:workspaceMembers] CHANGED size=${prev.length}->${next.length}`,
      );
    prevWorkspaceMembersRef.current = next;
    return next;
  }, [renderUserMap, wsMembers]);

  // Workspace channels for #channel mention highlighting
  const wsChannelsResult = useQuery(
    workspaceId ? queries.channels.byWorkspace(workspaceId) : null,
  );
  useQueryDebugLog("channels.byWorkspace", wsChannelsResult);
  const [wsChannelsRaw] = wsChannelsResult;
  const prevWorkspaceChannelsRef = useRef<ChannelLite[]>([]);
  const workspaceChannels = useMemo<ChannelLite[]>(() => {
    const next = (wsChannelsRaw ?? [])
      .filter(
        (ch) => ch.type !== "dm" && ch.type !== "group_dm" && !ch.is_archived,
      )
      .map((ch) => ({ id: ch.id, name: ch.name }));
    const prev = prevWorkspaceChannelsRef.current;
    if (
      next.length === prev.length &&
      next.every((n, i) => n.id === prev[i].id && n.name === prev[i].name)
    ) {
      if (RENDER_DEBUG)
        console.debug(`[hook:workspaceChannels] STABLE size=${next.length}`);
      return prev;
    }
    if (RENDER_DEBUG)
      console.debug(
        `[hook:workspaceChannels] CHANGED size=${prev.length}->${next.length}`,
      );
    prevWorkspaceChannelsRef.current = next;
    return next;
  }, [wsChannelsRaw]);

  const hasCollaborators = useMemo(() => {
    if (isDM || wsMembers.length === 0 || channelMemberList.length === 0)
      return false;
    const collabIds = new Set(
      wsMembers
        .filter((m) => (m as { role?: string }).role === "collaborator")
        .map((m) => m.user_id),
    );
    return channelMemberList.some((m) => collabIds.has(m.user_id));
  }, [isDM, wsMembers, channelMemberList]);

  // Active workspace-member user IDs for the Former-member badge. Built
  // from raw wsMembers (publication-filtered to removed_at IS NULL) so
  // deactivated-but-still-a-member users are NOT flagged as former —
  // they get the Deactivated pill instead. Scoped to the current
  // workspace; null for DM channels where "former member" doesn't apply.
  //
  // Gate on `wsMembersStatus.type === "complete"` so we don't flash the
  // pill during Zero's cold-cache hydration. While the query is still
  // "unknown", wsMembersRaw is an empty array indistinguishable from
  // "ready but empty" — returning an empty Set would make every message
  // (including the viewer's own) flash "Former member" until the query
  // resolves. Matches the pattern used by `hasCollaborators` above.
  //
  // Identity-stability guard: this Set flows into `MessageRow` as a prop.
  // Two fragility sources are guarded here:
  //   1. `useQuery` returns a fresh `status` object on every IVM emission
  //      even when `.type` is unchanged — we depend on `wsMembersStatus?.type`
  //      (primitive), not the object.
  //   2. `wsMembers` may swap identity while content is unchanged (e.g.
  //      when a sibling subscription reshapes its envelope) — we compare
  //      the next Set against the previous and return prev when content
  //      is equal so MessageRow.memo holds across cascades.
  const prevActiveWorkspaceMemberIdsRef = useRef<Set<string> | null>(null);
  const activeWorkspaceMemberIds = useMemo(() => {
    if (isDM) return null;
    if (!workspaceId) return null;
    if (wsMembersStatus?.type !== "complete") return null;
    const next = new Set(wsMembers.map((m) => m.user_id));
    const prev = prevActiveWorkspaceMemberIdsRef.current;
    if (prev && prev.size === next.size) {
      let same = true;
      for (const id of next) {
        if (!prev.has(id)) {
          same = false;
          break;
        }
      }
      if (same) {
        if (RENDER_DEBUG)
          console.debug(
            `[hook:activeWorkspaceMemberIds] STABLE size=${next.size}`,
          );
        return prev;
      }
    }
    if (RENDER_DEBUG)
      console.debug(
        `[hook:activeWorkspaceMemberIds] CHANGED size=${prev?.size ?? "init"}->${next.size}`,
      );
    prevActiveWorkspaceMemberIdsRef.current = next;
    return next;
  }, [isDM, workspaceId, wsMembers, wsMembersStatus?.type]);

  return {
    wsMembers,
    wsMembersStatus,
    workspaceMembers,
    workspaceChannels,
    hasCollaborators,
    activeWorkspaceMemberIds,
  };
}
